/**
 * Trainer Service
 * API calls for trainer dashboard, sessions, activities, and analytics
 */

import api from './api';

const trainerService = {
  // ==================== DASHBOARD ====================

  /**
   * Get trainer dashboard statistics
   * @returns {Promise} Dashboard stats including sessions, participants, and activity
   */
  getDashboardStats: async () => {
    return api.get('/trainer/dashboard');
  },

  // ==================== SESSIONS ====================

  /**
   * Get trainer's sessions
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (all, live, scheduled, completed)
   * @param {string} params.search - Search by name or code
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise} List of sessions with pagination and stats
   */
  getSessions: async (params = {}) => {
    return api.get('/trainer/sessions', params);
  },

  /**
   * Get single session details
   * @param {string} sessionId - Session ID
   * @returns {Promise} Session details with sub-sessions and activities
   */
  getSession: async (sessionId) => {
    return api.get(`/trainer/sessions/${sessionId}`);
  },

  /**
   * Update session setup (bulk update subsessions, activities, workbooks, mode)
   * @param {string} sessionId - Session ID
   * @param {Object} setupData - Setup data
   * @param {Array} setupData.subSessions - Sub-sessions with activities
   * @param {Array} setupData.activities - All activities
   * @param {Array} setupData.workbooks - Workbooks
   * @param {string} setupData.sessionMode - Session mode (drip or non-drip)
   * @returns {Promise} Updated session
   */
  updateSessionSetup: async (sessionId, setupData) => {
    return api.put(`/trainer/sessions/${sessionId}/setup`, setupData);
  },

  /**
   * Get session participants
   * @param {string} sessionId - Session ID
   * @returns {Promise} List of participants
   */
  getSessionParticipants: async (sessionId) => {
    return api.get(`/trainer/sessions/${sessionId}/participants`);
  },

  // ==================== SUB-SESSIONS ====================

  /**
   * Add sub-session to a session
   * @param {string} sessionId - Session ID
   * @param {Object} subSessionData - Sub-session details
   * @param {string} subSessionData.name - Sub-session name
   * @param {number} subSessionData.duration - Duration in minutes
   * @param {number} subSessionData.order - Order position
   * @returns {Promise} Created sub-session
   */
  addSubSession: async (sessionId, subSessionData) => {
    return api.post(`/trainer/sessions/${sessionId}/subsessions`, subSessionData);
  },

  /**
   * Update sub-session
   * @param {string} sessionId - Session ID
   * @param {string} subSessionId - Sub-session ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise} Updated sub-session
   */
  updateSubSession: async (sessionId, subSessionId, updateData) => {
    return api.put(`/trainer/sessions/${sessionId}/subsessions/${subSessionId}`, updateData);
  },

  /**
   * Delete sub-session
   * @param {string} sessionId - Session ID
   * @param {string} subSessionId - Sub-session ID
   * @returns {Promise} Success response
   */
  deleteSubSession: async (sessionId, subSessionId) => {
    return api.delete(`/trainer/sessions/${sessionId}/subsessions/${subSessionId}`);
  },

  // ==================== ACTIVITIES ====================

  /**
   * Add activity to sub-session
   * @param {string} sessionId - Session ID
   * @param {string} subSessionId - Sub-session ID
   * @param {Object} activityData - Activity details
   * @param {string} activityData.type - Activity type (poll, quiz, wordCloud, etc.)
   * @param {string} activityData.title - Activity title
   * @param {string} activityData.question - Question text
   * @param {Object} activityData.config - Activity-specific configuration
   * @param {number} activityData.timeLimit - Time limit in seconds
   * @returns {Promise} Created activity
   */
  addActivity: async (sessionId, subSessionId, activityData) => {
    return api.post(`/trainer/sessions/${sessionId}/subsessions/${subSessionId}/activities`, activityData);
  },

  /**
   * Update activity
   * @param {string} sessionId - Session ID
   * @param {string} subSessionId - Sub-session ID
   * @param {string} activityId - Activity ID
   * @param {Object} updateData - Fields to update
   * @returns {Promise} Updated activity
   */
  updateActivity: async (sessionId, subSessionId, activityId, updateData) => {
    return api.put(`/trainer/sessions/${sessionId}/subsessions/${subSessionId}/activities/${activityId}`, updateData);
  },

  /**
   * Delete activity
   * @param {string} sessionId - Session ID
   * @param {string} subSessionId - Sub-session ID
   * @param {string} activityId - Activity ID
   * @returns {Promise} Success response
   */
  deleteActivity: async (sessionId, subSessionId, activityId) => {
    return api.delete(`/trainer/sessions/${sessionId}/subsessions/${subSessionId}/activities/${activityId}`);
  },

  // ==================== WORKBOOKS ====================

  /**
   * Get all workbooks for trainer's sessions
   * @param {Object} params - Query parameters
   * @param {string} params.sessionId - Filter by session
   * @param {string} params.search - Search by name
   * @returns {Promise} List of workbooks
   */
  getWorkbooks: async (params = {}) => {
    return api.get('/trainer/workbooks', params);
  },

  /**
   * Upload workbook file to server and add to session
   * @param {string} sessionId - Session ID
   * @param {File} file - File to upload
   * @returns {Promise} Uploaded workbook data with permanent URL
   */
  uploadWorkbook: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    const uploadResult = await api.upload(`/upload/session/${sessionId}/document`, formData);

    console.log('[uploadWorkbook] Upload result:', uploadResult);

    // Validate that upload returned a valid URL
    if (!uploadResult.success || !uploadResult.data?.url) {
      console.error('[uploadWorkbook] Upload failed - no URL returned:', uploadResult);
      throw new Error('File upload failed - no URL returned from server. Please check S3 configuration.');
    }

    const url = uploadResult.data.url;
    if (!url.startsWith('http')) {
      console.error('[uploadWorkbook] Invalid URL returned:', url);
      throw new Error('File upload returned an invalid URL. Please check S3 configuration.');
    }

    // After uploading, add workbook reference to session
    const workbookData = {
      name: file.name,
      url: url,
      type: file.type || file.name.split('.').pop() || 'unknown',
      size: file.size,
    };

    console.log('[uploadWorkbook] Adding workbook to session:', workbookData);
    return api.post(`/trainer/sessions/${sessionId}/workbooks`, workbookData);
  },

  /**
   * Add workbook to session (with pre-existing URL)
   * @param {string} sessionId - Session ID
   * @param {Object} workbookData - Workbook details
   * @param {string} workbookData.name - File name
   * @param {string} workbookData.url - File URL
   * @param {string} workbookData.type - File type
   * @param {number} workbookData.size - File size in bytes
   * @returns {Promise} Created workbook
   */
  addWorkbook: async (sessionId, workbookData) => {
    return api.post(`/trainer/sessions/${sessionId}/workbooks`, workbookData);
  },

  /**
   * Delete workbook
   * @param {string} sessionId - Session ID
   * @param {string} workbookId - Workbook ID
   * @returns {Promise} Success response
   */
  deleteWorkbook: async (sessionId, workbookId) => {
    return api.delete(`/trainer/sessions/${sessionId}/workbooks/${workbookId}`);
  },

  /**
   * Upload a pre-read material (Phase 2 pre-session).
   * Returns { success, data: { name, url, type, size, uploadedAt } }.
   * Caller is responsible for persisting the material list to session.preSession.materials.
   * @param {string} sessionId - Session ID
   * @param {File} file - File to upload
   * @returns {Promise} Upload result
   */
  uploadPreRead: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload(`/upload/session/${sessionId}/pre-read`, formData);
  },

  /**
   * Launch workbook for participants
   * @param {string} sessionId - Session ID
   * @param {Object} workbook - Workbook to launch
   * @returns {Promise} Success response with launched workbook data
   */
  launchWorkbook: async (sessionId, workbook) => {
    return api.post(`/trainer/sessions/${sessionId}/workbook/launch`, { workbook });
  },

  /**
   * Stop the launched workbook
   * @param {string} sessionId - Session ID
   * @returns {Promise} Success response
   */
  stopWorkbook: async (sessionId) => {
    return api.post(`/trainer/sessions/${sessionId}/workbook/stop`);
  },

  // ==================== LIVE SESSION CONTROL ====================

  /**
   * Launch/Start session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Updated session with live status
   */
  launchSession: async (sessionId) => {
    return api.post(`/trainer/sessions/${sessionId}/launch`, {});
  },

  /**
   * Pause session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Updated session with paused status
   */
  pauseSession: async (sessionId) => {
    return api.post(`/trainer/sessions/${sessionId}/pause`, {});
  },

  /**
   * Resume a paused session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Updated session with live status
   */
  resumeSession: async (sessionId) => {
    return api.post(`/trainer/sessions/${sessionId}/resume`, {});
  },

  /**
   * End/Complete session
   * @param {string} sessionId - Session ID
   * @returns {Promise} Updated session with completed status
   */
  endSession: async (sessionId) => {
    return api.post(`/trainer/sessions/${sessionId}/end`, {});
  },

  /**
   * Launch activity
   * @param {string} sessionId - Session ID
   * @param {string} activityId - Activity ID
   * @returns {Promise} Updated activity with active status
   */
  launchActivity: async (sessionId, activityId) => {
    return api.post(`/trainer/sessions/${sessionId}/activities/${activityId}/launch`, {});
  },

  /**
   * Close activity
   * @param {string} sessionId - Session ID
   * @param {string} activityId - Activity ID
   * @param {number} responseCount - Number of responses received
   * @returns {Promise} Updated activity with completed status
   */
  closeActivity: async (sessionId, activityId, responseCount = 0) => {
    return api.post(`/trainer/sessions/${sessionId}/activities/${activityId}/close`, { responseCount });
  },

  /**
   * Mark a subsession as completed
   * @param {string} sessionId - Session ID
   * @param {string} subsessionId - Subsession ID
   * @returns {Promise} Completion confirmation
   */
  completeSubsession: async (sessionId, subsessionId) => {
    return api.put(`/trainer/sessions/${sessionId}/subsessions/${subsessionId}/complete`);
  },

  /**
   * Get activity responses
   * @param {string} sessionId - Session ID
   * @param {string} activityId - Activity ID
   * @returns {Promise} Activity responses with participant data
   */
  getActivityResponses: async (sessionId, activityId) => {
    return api.get(`/trainer/sessions/${sessionId}/activities/${activityId}/responses`);
  },

  // ==================== SCHEDULE ====================

  /**
   * Get trainer schedule
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date for range filter
   * @param {string} params.endDate - End date for range filter
   * @param {number} params.month - Month number (1-12)
   * @param {number} params.year - Year
   * @returns {Promise} Sessions grouped by date
   */
  getSchedule: async (params = {}) => {
    return api.get('/trainer/schedule', params);
  },

  // ==================== ANALYTICS ====================

  /**
   * Get trainer analytics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (7d, 30d, 90d, all)
   * @returns {Promise} Analytics data with metrics and trends
   */
  getAnalytics: async (params = {}) => {
    return api.get('/trainer/analytics', params);
  },

  /**
   * Phase 2: post-session aggregate analytics (action plans, manager
   * engagement, reflections). Mirrors clientService.getPhase2Analytics —
   * backend route delegates to the same controller scoped to the trainer's
   * own org so solo trainers see their own training outcomes.
   */
  getPhase2Analytics: async () => {
    return api.get('/trainer/analytics/phase2');
  },

  // ==================== FEEDBACK ====================

  // TODO: Re-enable when backend endpoint is built
  // /**
  //  * Get facilitation feedback for a session
  //  * @param {string} sessionId - Session ID
  //  * @returns {Promise} Facilitation feedback data
  //  */
  // getFacilitationFeedback: async (sessionId) => {
  //   return api.get(`/trainer/sessions/${sessionId}/facilitation-feedback`);
  // },

  // ==================== OVERALL SESSION FEEDBACK ====================

  /**
   * Launch overall session feedback
   * @param {string} sessionId - Session ID
   * @returns {Promise} Success response
   */
  launchOverallFeedback: async (sessionId) => {
    return api.post(`/sessions/${sessionId}/overall-feedback/launch`, {});
  },

  /**
   * End overall session feedback
   * @param {string} sessionId - Session ID
   * @returns {Promise} Success response
   */
  endOverallFeedback: async (sessionId) => {
    return api.post(`/sessions/${sessionId}/overall-feedback/end`, {});
  },

  /**
   * Get overall session feedback status and responses
   * @param {string} sessionId - Session ID
   * @returns {Promise} Feedback status and responses
   */
  getOverallFeedback: async (sessionId) => {
    return api.get(`/sessions/${sessionId}/overall-feedback`);
  },
};

export default trainerService;
